import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import type { SchemaManualOffer } from 'cpool-schemas';
import { schemaManualOffer } from 'cpool-schemas';
import type { SubmitErrorHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuTrash2 } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { OfferForm } from './OfferForm';
import { OfferModalShell } from './OfferModalShell';
import { useDeleteOffer } from '../hooks/useDeleteOffer';
import { useUpdateOffer } from '../hooks/useUpdateOffer';
import type { ManualAcceptedOffer } from '../types';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';

type EditOfferFormValues = SchemaManualOffer & { commissionAmount: number };

type Props = {
    /** `null` keeps the sidebar mounted while it slides out, so the animation is not cut off. */
    manualOffer: ManualAcceptedOffer | null;
    onClose: () => void;
    open: boolean;
};

const toFormValues = (manualOffer: ManualAcceptedOffer | null) => {
    if (!manualOffer) return {};

    return {
        ...(manualOffer as SchemaManualOffer),
        // Read model stores null for unset RSV fields, but schemaManualOffer only allows undefined — normalize both.
        applicant1ResidualDebtInsurance: manualOffer.applicant1ResidualDebtInsurance ?? undefined,
        applicant2ResidualDebtInsurance: manualOffer.applicant2ResidualDebtInsurance ?? undefined,
        commissionAmount:
            manualOffer.commissionResponseInPercent && manualOffer.creditAmount
                ? (manualOffer.creditAmount * manualOffer.commissionResponseInPercent) / 100
                : undefined,
        file: manualOffer.documents ?? undefined,
    } as EditOfferFormValues;
};

export const EditOfferModal: React.FC<Props> = ({ manualOffer, onClose, open }) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const canChangeManualOffer = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeManualOffer;
    const canDeleteManualOffer = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].deleteManualOffer;

    const { isPending: isUpdatePending, mutate: updateOfferMutation } = useUpdateOffer({
        aggregateId: leadId,
        id: manualOffer?.id ?? '',
    });
    const { isPending: isDeletePending, mutate: deleteOfferMutation } = useDeleteOffer({
        aggregateId: leadId,
        documentKey: manualOffer?.documents?.key,
        offerId: manualOffer?.id ?? '',
    });
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const methods = useForm<EditOfferFormValues>({
        defaultValues: toFormValues(manualOffer),
        resolver: zodResolver(schemaManualOffer),
    });

    // The modal stays mounted, so the form has to follow whichever offer is selected
    useEffect(() => {
        methods.reset(toFormValues(manualOffer));
    }, [manualOffer, methods]);

    const handleError: SubmitErrorHandler<EditOfferFormValues> = () => {
        toast.error(t('OFFER_MANAGEMENT.TOASTS.STATUS_UPDATE_FAILED'));
    };

    const updateOffer = () => {
        updateOfferMutation(
            { formData: methods.getValues() },
            {
                onSuccess: () => {
                    toast.success(t('OFFER_MANAGEMENT.TOASTS.STATUS_UPDATE_SUCCESS'));
                    onClose();
                },
            },
        );
    };

    const deleteOffer = () => {
        setIsDeleteConfirmOpen(false);
        deleteOfferMutation(undefined, {
            onSuccess: () => {
                toast.success(t('OFFER_MANAGEMENT.TOASTS.STATUS_DELETE_SUCCESS'));
                onClose();
            },
        });
    };

    return (
        <FormProvider {...methods}>
            <OfferModalShell
                description={t('OFFER_MANAGEMENT.MANUAL_OFFER.EDIT_DESCRIPTION')}
                isSaveDisabled={!canChangeManualOffer || !methods.formState.isDirty}
                isSubmitting={isUpdatePending || isDeletePending}
                onClose={onClose}
                onDelete={canDeleteManualOffer ? () => setIsDeleteConfirmOpen(true) : undefined}
                onSubmit={methods.handleSubmit(updateOffer, handleError)}
                open={open}
                title={t('OFFER_MANAGEMENT.MANUAL_OFFER.EDIT_TITLE')}
            >
                {manualOffer && <OfferForm isUpdateForm />}
            </OfferModalShell>
            <Modal
                open={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                title={t('OFFER_MANAGEMENT.MANUAL_OFFER.DELETE_CONFIRM.TITLE')}
                panelClassName="max-w-md"
            >
                <div className="flex flex-col gap-4 text-14 text-midnight">
                    <p>{t('OFFER_MANAGEMENT.MANUAL_OFFER.DELETE_CONFIRM.BODY')}</p>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            iconLeft={<LuCircleX className="h-4 w-4" />}
                            onClick={() => setIsDeleteConfirmOpen(false)}
                        >
                            {t('BUTTONS.CANCEL')}
                        </Button>
                        <Button iconLeft={<LuTrash2 className="h-4 w-4" />} onClick={deleteOffer}>
                            {t('OFFER_MANAGEMENT.MANUAL_OFFER.DELETE_CONFIRM.CONFIRM')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </FormProvider>
    );
};
